/**
 * CashFlowCharts — insight charts for the rebuilt dashboard:
 *   1. Monthly cash flow: Receipts (in) vs Payments (out)
 *   2. Transaction mix by voucher type (count)
 * Built on chart.js / react-chartjs-2 (already a project dependency).
 * ADDED — the original DashboardCharts component is left untouched.
 */

import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import './CashFlowCharts.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

const TYPE_COLORS = {
  Receipt: '#10b981',
  Payment: '#ef4444',
  Sales: '#3b82f6',
  Purchase: '#8b5cf6',
  Journal: '#f59e0b',
  Contra: '#06b6d4',
};

const monthKey = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return { key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, date: d };
};

const inr0 = (v) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v);

const CashFlowCharts = ({ vouchers = [] }) => {
  const cashFlow = useMemo(() => {
    const buckets = {};
    vouchers.forEach((v) => {
      const mk = monthKey(v.date);
      if (!mk) return;
      if (!buckets[mk.key]) buckets[mk.key] = { date: mk.date, receipts: 0, payments: 0 };
      const amt = Math.abs(Number(v.amount) || 0);
      if (v.type === 'Receipt' || v.type === 'Sales') buckets[mk.key].receipts += amt;
      else if (v.type === 'Payment' || v.type === 'Purchase') buckets[mk.key].payments += amt;
    });
    const keys = Object.keys(buckets).sort((a, b) => buckets[a].date - buckets[b].date).slice(-12);
    return {
      labels: keys.map((k) => buckets[k].date.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' })),
      receipts: keys.map((k) => buckets[k].receipts),
      payments: keys.map((k) => buckets[k].payments),
    };
  }, [vouchers]);

  const mix = useMemo(() => {
    const counts = {};
    vouchers.forEach((v) => {
      counts[v.type] = (counts[v.type] || 0) + 1;
    });
    const labels = Object.keys(counts);
    return {
      labels,
      data: labels.map((l) => counts[l]),
      colors: labels.map((l) => TYPE_COLORS[l] || '#94a3b8'),
    };
  }, [vouchers]);

  const barData = {
    labels: cashFlow.labels,
    datasets: [
      { label: 'Inflow (Receipts/Sales)', data: cashFlow.receipts, backgroundColor: '#10b981', borderRadius: 4 },
      { label: 'Outflow (Payments/Purchase)', data: cashFlow.payments, backgroundColor: '#ef4444', borderRadius: 4 },
    ],
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top', labels: { boxWidth: 12, font: { size: 11 } } },
      tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${inr0(ctx.parsed.y)}` } },
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 11 } } },
      y: { ticks: { callback: (v) => inr0(v), font: { size: 11 } }, grid: { color: '#f1f5f9' } },
    },
  };

  const doughnutData = {
    labels: mix.labels,
    datasets: [{ data: mix.data, backgroundColor: mix.colors, borderWidth: 2, borderColor: '#fff' }],
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '62%',
    plugins: {
      legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } },
      tooltip: { callbacks: { label: (ctx) => `${ctx.label}: ${ctx.parsed} vouchers` } },
    },
  };

  const hasData = vouchers && vouchers.length > 0;

  return (
    <section className="insight-charts">
      <div className="insight-card insight-card-wide">
        <div className="insight-header">
          <h3>Cash Flow — Inflow vs Outflow</h3>
          <p>Monthly receipts and sales against payments and purchases.</p>
        </div>
        <div className="insight-body">
          {hasData ? <Bar data={barData} options={barOptions} /> : <div className="insight-empty">No data for this range</div>}
        </div>
      </div>

      <div className="insight-card insight-card-narrow">
        <div className="insight-header">
          <h3>Transaction Mix</h3>
          <p>Voucher count by type.</p>
        </div>
        <div className="insight-body">
          {hasData ? <Doughnut data={doughnutData} options={doughnutOptions} /> : <div className="insight-empty">No data for this range</div>}
        </div>
      </div>
    </section>
  );
};

export default CashFlowCharts;
